import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/project/integrations/supabase/client";
import { jwtDecode } from "jwt-decode";
import AuthService from "@/services/APIs/AuthService";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  getTenantId,
  setLocalTenantId,
} from "@/lib/storeManager";

import { axiosConfig } from "@/services/axiosConfig";
import { AuthRes } from "@/utils/types/auth";

type AuthState = {
  user: any | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  fullName: string;
  login: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  fullName: "",
  login: async () => { },
  signOut: async () => { },
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [fullName, setFullName] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
    });

    const initAuth = async () => {
      try {
        const token = getAccessToken();
        const userInfo = getUserInfo();

        const tenantId = import.meta.env.VITE_X_TENANTID || "26694315-dcfb-4745-9dd0-3329dba3dfb2";
        if (tenantId && !getTenantId()) {
          setLocalTenantId(tenantId);
        }

        if (token && userInfo) {
          let userId = userInfo.id;
          if (!userId) {
            try {
              const decoded = jwtDecode<any>(token);
              userId = decoded.user_id || decoded.userId;
            } catch (err) {
              console.error("Failed to decode token on init:", err);
            }
          }
          setUser({ ...userInfo, id: userId });
          setFullName(`${userInfo.first_name || ""} ${userInfo.last_name || ""}`.trim() || userInfo.email || "Office User");
          setIsAdmin(userInfo.user_type === "admin" || userInfo.user_type === "superuser");
        }

        // Always check / initialize Supabase session so client queries work
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setSession(data.session);
        } else if (token) {
          const { data: anonData } = await supabase.auth.signInAnonymously();
          setSession(anonData.session);
        }
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAdmin,
        fullName,
        login: async (email: string, password: string) => {
          setLoading(true);
          try {
            const tenantId = import.meta.env.VITE_X_TENANTID || "26694315-dcfb-4745-9dd0-3329dba3dfb2";
            if (tenantId && !getTenantId()) {
              setLocalTenantId(tenantId);
            }

            const axiosResponse = await axiosConfig.post<AuthRes>("/auth/login/", { email, password });
            const response = axiosResponse.data;
            if (response) {
              const access = response.access;
              const refresh = response.refresh;
              setLocalAccessToken(access);
              setLocalRefreshToken(refresh);

              let userId = "";
              try {
                const decoded = jwtDecode<any>(access);
                userId = decoded.user_id || decoded.userId || "";
              } catch (err) {
                console.error("Failed to decode JWT:", err);
              }

              const userInfo = {
                first_name: response.user?.first_name || "",
                last_name: response.user?.last_name || "",
                email: response.user?.email || email,
                user_type: response.user?.user_type || "",
                id: userId,
              };

              setLocalUserInfo(userInfo);
              setUser(userInfo);
              setFullName(`${userInfo.first_name} ${userInfo.last_name}`.trim() || userInfo.email);
              setIsAdmin(userInfo.user_type === "admin" || userInfo.user_type === "superuser");

              const { data: sData } = await supabase.auth.getSession();
              if (!sData.session) {
                const { data: anonData } = await supabase.auth.signInAnonymously();
                setSession(anonData.session);
              } else {
                setSession(sData.session);
              }

              void queryClient.invalidateQueries();
            } else {
              throw new Error("Invalid credentials or empty response");
            }
          } finally {
            setLoading(false);
          }
        },
        signOut: async () => {
          setLoading(true);
          try {
            const refresh = getRefreshToken();
            if (refresh) {
              await AuthService.logout().catch(err => console.error("API logout error:", err));
            }
          } finally {
            clearLocalStorage();
            setUser(null);
            setSession(null);
            setFullName("");
            setIsAdmin(false);
            await supabase.auth.signOut().catch(err => console.error("Supabase sign out error:", err));
            setLoading(false);
          }
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);